/**
 * @fileoverview useLocalDatabase — React hook that wraps the localDatabaseManager
 *               and provides { db, status, canWrite, replicationStates, error }
 *               to app DB providers. Enforces auth-first DB opening, metadata
 *               validation, recovery, and background sync startup.
 * @module local-db/hooks/use-local-database
 *
 * Responsibilities:
 * - Create and cache a localDatabaseManager instance for the app.
 * - Wait for authentication before opening the DB.
 * - Wait for expected metadata (from web-config) before validating.
 * - Call manager.open() which validates _db_meta and executes recovery.
 * - Start background replication as soon as the DB is available (status
 *   'ready' OR 'syncing-pending-writes' — the latter so a pending
 *   wipe-and-rebuild recovery can actually flush unsynced writes before
 *   the local database is deleted).
 * - Subscribe to manager status changes and expose them as React state.
 * - Cancel replication and reset on logout.
 * - Clear pull checkpoints on fresh authenticated session (for tenant/mobile).
 *
 * Dependencies:
 * - react (useState, useEffect, useRef, useCallback)
 * - ../services/localDatabaseManager (createLocalDatabaseManager, canWriteForStatus)
 * - ../types/dbMeta (DbStatus, ExpectedDbMeta, DbId)
 * - ../replication/replicationFactory (cancelAllReplication, NamedReplicationStates)
 *
 * Architecture:
 * - Auth-first: DB never opens until isAuthenticated is true.
 * - Metadata-aware: manager validates _db_meta against expected metadata.
 * - Recovery: manager handles env bleed, schema drift, and app version mismatches.
 * - Sync-gated: replication starts only after manager reports 'ready'.
 * - Status-driven: db is non-null only when status is 'syncing-pending-writes' or 'ready'.
 *
 * Notes:
 * - This hook replaces useAuthGatedDb in apps that adopt the localDatabaseManager.
 * - useAuthGatedDb remains for backward compatibility until all providers migrate.
 * - The hook uses refs for callbacks (same pattern as useAuthGatedDb) to prevent
 *   effect re-firing from inline function identity changes.
 *
 * @logging
 * Events:    local_db_status_change (info), local_db_open (info),
 *            local_db_sync_start (info), local_db_open_failed (error).
 * Level:     info / warn / error.
 * Correlation: dbId via global telemetry properties.
 *
 * @errorHandling
 * Strategy:  Catch manager.open / startSync errors, set status='error', and track
 *            the exception before allowing a retry on the next auth change.
 * User Impact: UI shows 'error' status; the app can prompt for retry.
 * Logging:   trackException for thrown errors; no sensitive data in properties.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { WebTelemetry } from '@firesquirrel/web-telemetry';
import type { NamedReplicationStates } from '../replication/replicationFactory';
import { cancelAllReplication } from '../replication/replicationFactory';
import {
  createLocalDatabaseManager,
  canWriteForStatus,
  UpdateAppFirstError,
  type LocalDatabaseManager,
  type LocalDatabaseManagerConfig,
} from '../services/localDatabaseManager';
import type { DbStatus, ExpectedDbMeta, DbId } from '../types/dbMeta';

// ---------------------------------------------------------------------------
// Options + result interfaces
// ---------------------------------------------------------------------------

/**
 * @contract UseLocalDatabaseOptions
 *
 * Purpose:
 * - Configuration for the useLocalDatabase hook.
 *
 * Fields:
 * - `isAuthenticated`: From the app's auth provider — DB + sync will not start until true.
 * - `dbId`: Which app this hook manages ('system-web' | 'tenant-web' | 'mobile-pwa').
 * - `managerConfig`: Configuration for creating the localDatabaseManager instance.
 * - `expectedMetadata`: Server-authoritative expected DB metadata from web-config (null if not yet loaded).
 * - `isOnline`: Whether the app is online (affects recovery decision — offline uses compiled-in constants).
 * - `startSync`: Called after DB opens + recovery completes. Returns replication states.
 * - `collectionKeys`: Dexie table names — when provided, pull checkpoints are cleared on
 *   every fresh authenticated session (same behavior as useAuthGatedDb).
 * - `onStatusChange`: Optional callback for logging/metrics on status transitions.
 *
 * Rules:
 * - `managerConfig` must include stable function references (getXxxDb, destroyXxxDb, etc.).
 * - `expectedMetadata` may be null on first render (web-config not yet loaded).
 *   The hook waits for it before calling manager.open(), unless `allowOfflineFallback`
 *   is true (mobile can proceed with compiled-in constants).
 *
 * Sync:
 * - Not synced — hook configuration.
 */
export interface UseLocalDatabaseOptions<Db> {
  isAuthenticated: boolean;
  dbId: DbId;
  managerConfig: LocalDatabaseManagerConfig<Db>;
  expectedMetadata: ExpectedDbMeta | null;
  isOnline?: boolean;
  startSync?: (db: Db) => Promise<NamedReplicationStates | null> | NamedReplicationStates | null;
  collectionKeys?: string[];
  onStatusChange?: (status: DbStatus) => void;
  /**
   * Optional shared telemetry client. When provided, status changes and errors are
   * tracked through the WebTelemetry client.
   */
  telemetry?: WebTelemetry;
  /**
   * If true, the hook proceeds with manager.open() even when expectedMetadata is null
   * (uses compiled-in constants as fallback). Mobile sets this to true so field
   * inspectors can work offline without waiting for web-config. Web apps should
   * leave this false — they always have web-config before auth completes.
   */
  allowOfflineFallback?: boolean;
  /**
   * Fallback timeout in milliseconds. When expectedMetadata is null and
   * allowOfflineFallback is false, the hook waits this long for web-config to
   * load before falling back to compiled-in constants (passes null to
   * manager.open, which uses compiled-in schema version + aligns environment/
   * appVersion/apiBaseUrl with local _db_meta to avoid false wipes).
   * Default: 10_000 (10 seconds). Set to 0 to disable the timeout (wait forever).
   */
  metadataFallbackTimeoutMs?: number;
}

/**
 * @contract UseLocalDatabaseResult
 *
 * Purpose:
 * - The state exposed by the useLocalDatabase hook to app DB providers.
 *
 * Fields:
 * - `status`: Current DB lifecycle status ('initializing' | 'updating-app' | 'syncing-pending-writes' | 'recovering' | 'ready' | 'error').
 * - `db`: The DB instance (non-null only when status is 'syncing-pending-writes' or 'ready').
 * - `canWrite`: Whether writes are currently allowed (true only during 'syncing-pending-writes' and 'ready').
 * - `replicationStates`: Active replication state map (null until sync starts).
 * - `error`: Error message if recovery or open failed (null otherwise).
 *
 * Rules:
 * - Components should check `canWrite` before enabling save buttons.
 * - Components should check `db` is non-null before reading data.
 * - The sync widget should subscribe to `status` for recovery UI.
 *
 * Sync:
 * - Local lifecycle state — not synced.
 */
export interface UseLocalDatabaseResult<Db> {
  status: DbStatus;
  db: Db | null;
  canWrite: boolean;
  replicationStates: NamedReplicationStates | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

/**
 * @hook useLocalDatabase
 *
 * Purpose:
 * - Wraps the localDatabaseManager and provides reactive { db, status, canWrite }
 *   state to app DB providers. Enforces auth-first opening, metadata validation,
 *   recovery, and background sync startup.
 *
 * Returns:
 * - UseLocalDatabaseResult: { status, db, canWrite, replicationStates, error }
 *
 * Side Effects:
 * - Opens the Dexie database via the manager on first authenticated session.
 * - Starts background replication after the DB is ready.
 * - Cancels replication and resets on logout.
 * - Clears pull checkpoints on fresh session when collectionKeys is provided.
 * - Subscribes to manager status changes and syncs them to React state.
 *
 * Offline Behavior:
 * - When isOnline is false and expectedMetadata is null, the hook waits if
 *   allowOfflineFallback is false (web apps). If allowOfflineFallback is true
 *   (mobile), the hook proceeds with compiled-in constants.
 * - When isOnline is false and expectedMetadata is available, the manager uses
 *   the expected metadata with compiled-in constants as the decision authority.
 *
 * @logging
 * Events:   local_db_open (info), local_db_sync_start (info),
 *           local_db_open_failed (error), local_db_status_change (info).
 * Level:    info / warn / error.
 * Correlation: dbId, environment, schemaVersion via global telemetry properties.
 *
 * @errorHandling
 * Strategy: Catch manager.open / startSync errors, set status='error', and track
 *           the exception. The next auth change (re-login) resets initDone and
 *           allows a retry. If the lifecycle effect is re-run while an open() is
 *           still in flight (e.g. expectedMetadata identity changes mid-open),
 *           the in-flight open is cancelled and initDone is released so the
 *           superseding run can retry — preventing the DB from being stranded
 *           in 'initializing' with no open in flight.
 * User Impact: UI shows 'error' with a human-readable message.
 * Logging:  trackException for thrown errors; no sensitive values logged.
 */
export function useLocalDatabase<Db>({
  isAuthenticated,
  dbId,
  managerConfig,
  expectedMetadata,
  isOnline = true,
  startSync,
  collectionKeys,
  onStatusChange,
  telemetry,
  allowOfflineFallback = false,
  metadataFallbackTimeoutMs = 10_000,
}: UseLocalDatabaseOptions<Db>): UseLocalDatabaseResult<Db> {
  const [status, setStatus] = useState<DbStatus>('initializing');
  const [db, setDb] = useState<Db | null>(null);
  const [replicationStates, setReplicationStates] = useState<NamedReplicationStates | null>(null);
  const [error, setError] = useState<string | null>(null);
  // syncRetryCount state ensures that if a background startSync call is
  // cancelled mid-flight by a dependency change, the superseding effect
  // run (which bailed due to syncStartInFlightRef) can be re-triggered to
  // try again once the first call finishes.
  const [syncRetryCount, setSyncRetryCount] = useState(0);
  // State (not a ref) so that firing the fallback triggers a re-render and
  // the main lifecycle effect re-runs with fallbackFired=true. A ref would
  // silently set the flag without re-invoking the effect, leaving the DB
  // stuck in 'initializing' forever.
  const [metadataFallbackFired, setMetadataFallbackFired] = useState(false);

  // Refs for stable identity (same pattern as useAuthGatedDb)
  const managerRef = useRef<LocalDatabaseManager<Db> | null>(null);
  const dbRef = useRef<Db | null>(null);
  const replicationRef = useRef<NamedReplicationStates | null>(null);
  // Tracks the exact `db` instance `replicationRef.current` was started
  // against. A wipe-and-rebuild recovery closes the pre-wipe Dexie instance
  // and opens a brand new one while replication remains bound to the old
  // instance (replicationRef.current is only cleared on logout/unmount).
  // Comparing against this ref lets the background replication effect below
  // detect that `db` now points at a different instance and rebind, instead
  // of returning early forever because `replicationRef.current` is truthy.
  const replicatedDbRef = useRef<Db | null>(null);
  // Tracks the `startSync` callback identity used for the last successful
  // or attempted replication start. Combined with replicatedDbRef, this
  // prevents infinite retry loops when startSync returns null (e.g. token
  // still missing) while allowing a retry when the callback identity
  // changes (e.g. token arrived and startSync was re-memoized).
  const replicatedStartSyncRef = useRef<unknown>(null);
  const initDone = useRef(false);
  const syncStartInFlightRef = useRef(false);

  // Store callbacks in refs so the main effect only depends on isAuthenticated
  const startSyncRef = useRef(startSync);
  const collectionKeysRef = useRef(collectionKeys);
  const onStatusChangeRef = useRef(onStatusChange);
  const expectedMetadataRef = useRef(expectedMetadata);
  const isOnlineRef = useRef(isOnline);
  const allowOfflineFallbackRef = useRef(allowOfflineFallback);
  const metadataFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  startSyncRef.current = startSync;
  collectionKeysRef.current = collectionKeys;
  onStatusChangeRef.current = onStatusChange;
  expectedMetadataRef.current = expectedMetadata;
  isOnlineRef.current = isOnline;
  allowOfflineFallbackRef.current = allowOfflineFallback;

  // Content-derived key for expectedMetadata. The main lifecycle effect depends
  // on this rather than the object identity: consumers that rebuild the
  // web-config object each render would otherwise re-run the effect on every
  // render, and any re-run while manager.open() is in flight cancels that open
  // (discarding its result — including a failure that should surface as
  // status='error'). ExpectedDbMeta is five flat primitives, so this key is a
  // lossless stand-in for a deep comparison.
  const expectedMetadataKey = expectedMetadata
    ? `${expectedMetadata.dbId}|${expectedMetadata.environment}|${expectedMetadata.schemaVersion}|${expectedMetadata.appVersion}|${expectedMetadata.apiBaseUrl}`
    : null;

  // Create the manager once (config functions are stable)
  if (!managerRef.current) {
    managerRef.current = createLocalDatabaseManager<Db>({
      ...managerConfig,
      telemetry,
    });
  }

  const updateStatus = useCallback((newStatus: DbStatus) => {
    setStatus(newStatus);
    onStatusChangeRef.current?.(newStatus);
    telemetry?.trackEvent('local_db_status_change', {
      dbId,
      status: newStatus,
    });
  }, [dbId, telemetry]);

  // Subscribe to manager status changes → React state
  useEffect(() => {
    const manager = managerRef.current;
    if (!manager) return;
    const unsubscribe = manager.subscribeStatus((newStatus) => {
      updateStatus(newStatus);
      // Update dbRef/db state when status transitions to/from available states
      if (newStatus === 'ready' || newStatus === 'syncing-pending-writes') {
        const currentDb = manager.getDb();
        dbRef.current = currentDb;
        setDb(currentDb);
      } else {
        dbRef.current = null;
        setDb(null);
      }
    });
    return unsubscribe;
  }, [updateStatus]);

  // Main lifecycle: open DB + start sync when authenticated
  // Runs when auth, expectedMetadata, or offline fallback changes. initDone
  // prevents a second open on later renders / StrictMode double-runs.
  useEffect(() => {
    if (!isAuthenticated) {
      // Auth lost (logout) — cancel replication, reset for next login
      if (replicationRef.current) {
        cancelAllReplication(replicationRef.current);
        replicationRef.current = null;
        replicatedDbRef.current = null;
        replicatedStartSyncRef.current = null;
        setReplicationStates(null);
      }
      // Clear any pending metadata fallback timeout and reset the fired flag
      if (metadataFallbackTimeoutRef.current) {
        clearTimeout(metadataFallbackTimeoutRef.current);
        metadataFallbackTimeoutRef.current = null;
      }
      if (metadataFallbackFired) setMetadataFallbackFired(false);
      initDone.current = false;
      dbRef.current = null;
      setDb(null);
      setError(null);
      updateStatus('initializing');
      return;
    }

    // Already initialized — skip (prevents StrictMode double-run)
    if (initDone.current) return;

    // If metadata isn't loaded yet (and we aren't allowed to fall back),
    // set a timeout to fall back to compiled-in constants. The timeout is
    // cleared when metadata arrives (the effect re-runs because expectedMetadata
    // is in the deps array) or when auth is lost. When the timeout fires,
    // setMetadataFallbackFired(true) triggers a re-render so the effect
    // re-runs and proceeds with null metadata.
    const expected = expectedMetadataRef.current;
    const online = isOnlineRef.current;
    const allowFallback = allowOfflineFallbackRef.current;
    if (!expected && !allowFallback && !metadataFallbackFired) {
      telemetry?.trackEvent('local_db_waiting_for_metadata', { dbId });
      // Set a fallback timeout — if web-config doesn't arrive in time, proceed
      // with null metadata (the manager uses compiled-in constants and aligns
      // environment/apiBaseUrl/appVersion with local _db_meta to avoid wipes).
      if (metadataFallbackTimeoutMs > 0 && !metadataFallbackTimeoutRef.current) {
        metadataFallbackTimeoutRef.current = setTimeout(() => {
          metadataFallbackTimeoutRef.current = null;
          console.warn(`[useLocalDatabase] Metadata fallback timeout (${metadataFallbackTimeoutMs}ms) — proceeding with compiled-in constants`);
          telemetry?.trackEvent('local_db_metadata_fallback_timeout', { dbId, timeoutMs: String(metadataFallbackTimeoutMs) });
          // Trigger a re-render so the effect re-runs with the fallback fired.
          setMetadataFallbackFired(true);
        }, metadataFallbackTimeoutMs);
      }
      return;
    }

    // Metadata arrived (or fallback fired) — clear any pending timeout
    if (metadataFallbackTimeoutRef.current) {
      clearTimeout(metadataFallbackTimeoutRef.current);
      metadataFallbackTimeoutRef.current = null;
    }

    // Mark as in-flight; from here on the async work owns the lifecycle.
    initDone.current = true;

    let cancelled = false;
    // Tracks whether the async open below has finished. Read by the cleanup to
    // decide if initDone must be released so a superseding effect run can retry.
    let settled = false;

    (async () => {
      try {
        const manager = managerRef.current!;
        const openedDb = await manager.open(expected, online);
        if (cancelled) return;

        // Use manager.getDb() so holdWrites/recovering keeps db null even when
        // open() returns a valid Dexie instance.
        const currentDb = manager.getDb();
        dbRef.current = currentDb;
        setDb(currentDb);

        // Clear pull checkpoints on fresh session (same behavior as useAuthGatedDb)
        const keys = collectionKeysRef.current;
        if (keys?.length) {
          for (const key of keys) {
            try { localStorage.removeItem(`sync_pull_${key}`); } catch { /* ignore */ }
            try { localStorage.removeItem(`sync_fullpull_cursor_${key}`); } catch { /* ignore */ }
          }
          telemetry?.trackEvent('local_db_pull_checkpoints_cleared', { dbId });
        }

        // Manager already set status via its internal setStatus,
        // which propagated through the subscription in the other effect.
        // But if the subscription hasn't fired yet, ensure React state is current.
        if (!cancelled) {
          setError(null);
          const currentStatus = manager.getStatus();
          updateStatus(currentStatus);
        }
      } catch (err: any) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error(String(err));
          telemetry?.trackException(error, {
            dbId,
            operation: 'useLocalDatabase.open',
          });
          updateStatus('error');
          setError(error.message);
          // UpdateAppFirstError means the hard navigation was supposed to happen
          // but didn't. If retryable (offline / no callback), release initDone
          // so the effect re-runs when online or auth/metadata changes. If NOT
          // retryable (navigation attempted but didn't commit), latch initDone
          // — retrying manager.open() on the same deps just flashes between
          // splash and error. The user must manually refresh the page.
          if (err instanceof UpdateAppFirstError && !err.retryable) {
            telemetry?.trackEvent('local_db_update_app_first_blocked', { dbId });
          } else {
            initDone.current = false;
          }
        }
      } finally {
        settled = true;
      }
    })();

    return () => {
      cancelled = true;
      // If this run is superseded/unmounted while open() is still in flight, its
      // result will be discarded by the `cancelled` guards above. Release
      // initDone so the superseding run is not blocked by the early return at
      // the top of this effect — otherwise the DB would be stranded in
      // 'initializing' with no open in flight and no path to retry.
      if (!settled) initDone.current = false;
    };
  }, [isAuthenticated, expectedMetadataKey, allowOfflineFallback, metadataFallbackTimeoutMs, metadataFallbackFired, updateStatus, dbId, telemetry]);

  // Background replication lifecycle: starts when DB is ready.
  //
  // NOTE: syncing-pending-writes is a temporary recovery state where the
  // manager has opened the EXISTING (potentially wrong-env) database and is
  // waiting for config.syncPendingWrites() to finish before wiping. We
  // DELIBERATELY skip starting background replication here and let the
  // `syncPendingWrites` callback drive its own self-contained flush
  // replication against the pre-wipe instance. This avoids two independent
  // replication pipelines running concurrently against the same Dexie instance
  // (the host one and the flush one) and ensures the host sync only ever
  // binds to the final post-wipe database instance.
  useEffect(() => {
    if (!isAuthenticated || !db) {
      return;
    }

    if (status !== 'ready') return;

    // Already bound to this exact db instance AND sync function — nothing to do.
    if (
      replicationRef.current &&
      replicatedDbRef.current === db &&
      replicatedStartSyncRef.current === startSync
    ) {
      return;
    }

    let cancelled = false;
    (async () => {
      // Prevent parallel starts for the SAME instance (e.g. StrictMode).
      // If dependencies change while a start is in flight, the `cancelled`
      // check below will discard the stale result. When the in-flight call
      // finishes and sets `syncStartInFlightRef` back to false, it checks if
      // its own result was discarded by the `cancelled` flag — if so, it
      // increments `syncRetryCount` state to trigger a re-render so the
      // superseding effect run (which bailed due to this guard) can try again.
      if (syncStartInFlightRef.current) return;

      syncStartInFlightRef.current = true;
      telemetry?.trackEvent('local_db_sync_start', { dbId, status });
      try {
        // If replication is bound to a stale db instance (e.g. `db` changed
        // because a wipe-and-rebuild opened a fresh Dexie instance) OR a
        // different sync configuration (startSync changed), tear it down
        // before rebinding — otherwise we leak the previous sync connection.
        if (
          replicationRef.current &&
          (replicatedDbRef.current !== db || replicatedStartSyncRef.current !== startSync)
        ) {
          await cancelAllReplication(replicationRef.current);
          replicationRef.current = null;
          replicatedDbRef.current = null;
          replicatedStartSyncRef.current = null;
          setReplicationStates(null);
        }

        const states = (await startSyncRef.current?.(db)) ?? null;

        if (cancelled) {
          // This invocation was superseded. Tear down any states it created
          // so they don't leak or race with the next successful start.
          if (states) await cancelAllReplication(states);
          return;
        }

        // Record that we've attempted sync for this DB instance + sync identity.
        // A retry will only be triggered if `db` or `startSync` identity
        // changes (the latter happens when e.g. a missing auth token arrives).
        replicatedDbRef.current = db;
        replicatedStartSyncRef.current = startSync;

        if (states) {
          replicationRef.current = states;
          setReplicationStates(states);
        } else {
          replicationRef.current = null;
          setReplicationStates(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error(String(err));
          telemetry?.trackException(error, {
            dbId,
            operation: 'useLocalDatabase.startSync',
            status,
          });
          updateStatus('error');
          setError(error.message);
        }
      } finally {
        syncStartInFlightRef.current = false;
        // Self-healing re-run: if we were cancelled, it means a newer render
        // happened and its effect bailed because we were in flight. Kick a
        // re-render so that effect can run now that we've cleared the ref.
        if (cancelled) setSyncRetryCount((c) => c + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, db, status, dbId, telemetry, updateStatus, startSync, syncRetryCount]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (replicationRef.current) {
        cancelAllReplication(replicationRef.current);
        replicationRef.current = null;
      }
      if (metadataFallbackTimeoutRef.current) {
        clearTimeout(metadataFallbackTimeoutRef.current);
        metadataFallbackTimeoutRef.current = null;
      }
    };
  }, []);

  const canWrite = canWriteForStatus(status);

  return { status, db, canWrite, replicationStates, error };
}
